import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MapPin, Phone, Send, CheckCircle, Loader2, ArrowRight, ArrowUp, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'terms' | 'privacy' | null

  // Terms & Conditions Content
  const termsContent = {
    title: "Terms & Conditions",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "1. Introduction",
        content: "Welcome to my portfolio website. By accessing and using this website, you accept and agree to be bound by the terms and provisions of this agreement. These terms apply to all visitors, users, and others who access or use this website."
      },
      {
        heading: "2. Services",
        content: "I provide web development, AI-powered solutions, digital marketing, and branding services. All services are provided on a project basis with terms agreed upon before commencement. Project timelines, deliverables, and pricing will be outlined in separate agreements or proposals."
      },
      {
        heading: "3. Intellectual Property",
        content: "All content on this website, including text, graphics, logos, images, and software, is my property or licensed to me and is protected by copyright laws. Upon full payment, clients receive rights to use deliverables as agreed. Source code ownership will be specified in individual project agreements."
      },
      {
        heading: "4. Client Responsibilities",
        content: "Clients are responsible for providing accurate information, timely feedback, and necessary materials for project completion. Delays caused by client-side issues may affect project timelines and costs."
      },
      {
        heading: "5. Payment Terms",
        content: "Payment terms will be specified in project proposals. Typically, projects require an upfront deposit before work begins. Final payment is due upon project completion or as outlined in the agreement."
      },
      {
        heading: "6. Confidentiality",
        content: "I respect the confidentiality of all client information and project details. Sensitive information shared during our collaboration will not be disclosed to third parties without consent."
      },
      {
        heading: "7. Limitation of Liability",
        content: "I strive to deliver high-quality work but cannot guarantee specific business outcomes. I am not liable for indirect, incidental, or consequential damages arising from the use of my services."
      },
      {
        heading: "8. Contact",
        content: "For any questions regarding these terms, please contact me through the contact form on this website or via email."
      }
    ]
  };

  // Privacy Policy Content
  const privacyContent = {
    title: "Privacy Policy",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "1. Information We Collect",
        content: "When you use our contact form, we collect your name, email address, phone number (optional), and message content. We also automatically collect certain information including your IP address, browser type, and approximate location for analytics purposes."
      },
      {
        heading: "2. How We Use Your Information",
        content: "We use the information collected to respond to your inquiries, provide requested services, improve our website and services, and communicate about projects and opportunities. We do not sell or rent your personal information to third parties."
      },
      {
        heading: "3. Data Storage & Security",
        content: "Your data is stored securely using industry-standard encryption and security measures. We retain contact form submissions to maintain communication history and improve our services. You may request deletion of your data at any time."
      },
      {
        heading: "4. Cookies & Tracking",
        content: "This website may use cookies and similar tracking technologies to enhance user experience and analyze website traffic. You can control cookie preferences through your browser settings."
      },
      {
        heading: "5. Third-Party Services",
        content: "We may use third-party services for analytics, hosting, and communication. These services have their own privacy policies, and we encourage you to review them. We only share information necessary for these services to function."
      },
      {
        heading: "6. Your Rights",
        content: "You have the right to access, correct, or delete your personal information. You may also request a copy of the data we hold about you. To exercise these rights, please contact us through the provided channels."
      },
      {
        heading: "7. Children's Privacy",
        content: "This website is not intended for children under 13 years of age. We do not knowingly collect personal information from children."
      },
      {
        heading: "8. Changes to This Policy",
        content: "We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date. Continued use of the website after changes constitutes acceptance of the updated policy."
      },
      {
        heading: "9. Contact Us",
        content: "If you have any questions about this Privacy Policy or our data practices, please contact us through the contact form or email provided on this website."
      }
    ]
  };
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        message: `Phone: ${formData.phone}\nSubject: ${formData.subject}\n\n${formData.message}`
      });
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Background accents */}
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-1" style={{ top: '-30px', right: '-80px', width: '280px', height: '280px' }} />
      <div className="light-decor-blob-3" style={{ bottom: '15%', left: '2%', width: '220px', height: '220px' }} />
      <div className="light-decor-circle-2" style={{ top: '25%', left: '8%', width: '65px', height: '65px' }} />
      <div className="light-decor-dot-2" style={{ top: '10%', right: '25%' }} />
      <div className="light-decor-line-2" style={{ bottom: '25%', right: '10%', width: '60px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Contact Card */}
        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden"
          style={{
            background: 'hsl(var(--card))',
          }}
        >
          {/* Animated border effect */}
          <div
            className="absolute inset-0 rounded-3xl transition-opacity duration-300 pointer-events-none"
            style={{
              background: isHovering
                ? `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, hsl(var(--primary) / 0.5), transparent 40%)`
                : 'transparent',
            }}
          />
          
          {/* Border */}
          <div
            className="absolute inset-[1px] rounded-3xl pointer-events-none"
            style={{
              background: isHovering
                ? `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, hsl(var(--primary) / 1), hsl(var(--border)) 50%)`
                : 'hsl(var(--border))',
            }}
          />
          
          {/* Inner content background */}
          <div className="absolute inset-[2px] rounded-3xl bg-card" />

          {/* Content */}
          <div className="relative z-10 p-8 lg:p-12">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground tracking-wider">GET IN TOUCH</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
              {/* Left Side - Info */}
              <div>
                <h3 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-foreground leading-tight mb-4">
                  Work With Me - <span className="text-gradient">No Risk!</span>
                </h3>
                
                <div className="space-y-3 mb-8">
                  <p className="text-muted-foreground leading-relaxed">
                    Share your idea first. I'll create a prototype or demo to showcase the solution.
                  </p>
                  <p className="text-foreground font-medium">
                    Proceed only if you're satisfied—no upfront payment, no risk.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-base text-muted-foreground font-medium">E-mail:</p>
                      <p className="text-foreground font-medium">hello@mustakimahmed.com</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-base text-muted-foreground font-medium">Location:</p>
                      <p className="text-foreground font-medium">Sylhet, Bangladesh</p>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-base text-muted-foreground font-medium">Contact:</p>
                      <p className="text-foreground font-medium">+880 1995 432873</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Row 1: Name & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      name="name"
                      type="text"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                    />
                    <Input
                      name="phone"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                    />
                  </div>

                  {/* Row 2: Email & Subject */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      name="email"
                      type="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                    />
                    <Input
                      name="subject"
                      type="text"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                    />
                  </div>

                  {/* Message */}
                  <Textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-transparent border-border/50 rounded-xl px-4 py-3 placeholder:text-muted-foreground/60 focus:border-primary resize-none"
                  />

                  {/* Submit Button */}
                  {submitStatus !== 'success' ? (
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 rounded-full bg-gradient-primary hover:opacity-90 text-primary-foreground font-semibold text-base group"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  ) : null}

                  {/* Success Message - Beautiful Thank You */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="text-center py-8 px-4"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                        className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30"
                      >
                        <CheckCircle className="w-10 h-10 text-white" />
                      </motion.div>
                      <motion.h3
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-2xl sm:text-3xl font-bold text-foreground mb-3"
                      >
                        Thank You! 🎉
                      </motion.h3>
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg text-muted-foreground mb-2"
                      >
                        Your message has been sent successfully!
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="text-sm text-primary font-medium"
                      >
                        I'll get back to you as soon as possible.
                      </motion.p>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm text-center"
                    >
                      Failed to send message. Please try again.
                    </motion.div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="mt-20 pt-8 border-t border-border/50"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-base text-muted-foreground font-medium">
            <p>© Mustakim Ahmed 2025 | All Rights Reserved</p>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => setActiveModal('terms')}
                className="hover:text-foreground transition-colors cursor-pointer"
              >
                Terms & Condition
              </button>
              <button 
                onClick={() => setActiveModal('privacy')}
                className="hover:text-foreground transition-colors cursor-pointer"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setActiveModal('contact')}
                className="hover:text-foreground transition-colors cursor-pointer"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* Floating Buttons */}
      <div className="fixed right-6 bottom-6 z-50 flex flex-col gap-3">
        {/* WhatsApp Button */}
        <motion.a
          href="https://wa.me/+8801995432873"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
        >
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </motion.a>

        {/* Scroll to Top Button */}
        <motion.button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
        >
          <ArrowUp className="w-6 h-6 text-primary-foreground" />
        </motion.button>
      </div>

      {/* Modal for Terms, Privacy & Contact */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setActiveModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className={`relative w-full max-h-[90vh] ${activeModal === 'contact' ? 'max-w-xl' : 'max-w-4xl'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Outer container for border effect - thicker border */}
              <div className="relative rounded-2xl p-[3px]" style={{ overflow: 'hidden' }}>
                {/* Rotating Conic Gradient Border - brighter and more visible */}
                <motion.div 
                  className="absolute"
                  style={{
                    width: '200%',
                    height: '200%',
                    left: '-50%',
                    top: '-50%',
                    background: 'conic-gradient(from 0deg at 50% 50%, hsl(350, 100%, 60%) 0deg, hsl(340, 90%, 55%) 30deg, hsl(320, 85%, 50%) 60deg, hsl(300, 70%, 45%) 90deg, transparent 150deg, transparent 210deg, hsl(20, 90%, 55%) 270deg, hsl(10, 95%, 58%) 300deg, hsl(350, 100%, 60%) 360deg)',
                  }}
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                {/* Glass Inner Content */}
                <div className="relative z-10 modal-content backdrop-blur-xl rounded-[13px] overflow-hidden">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-white/10 modal-header">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">
                        {activeModal === 'terms' ? termsContent.title : 
                         activeModal === 'privacy' ? privacyContent.title : 
                         'Get In Touch'}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {activeModal === 'terms' ? `Last updated: ${termsContent.lastUpdated}` : 
                         activeModal === 'privacy' ? `Last updated: ${privacyContent.lastUpdated}` : 
                         "We'd love to hear from you"}
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="w-10 h-10 rounded-full border border-white/20 modal-close-btn flex items-center justify-center hover:bg-white/10 transition-colors"
                    >
                      <X className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {activeModal === 'contact' ? (
                      /* Contact Form */
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Input
                            name="name"
                            type="text"
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                          />
                          <Input
                            name="phone"
                            type="tel"
                            placeholder="Phone Number"
                            value={formData.phone}
                            onChange={handleChange}
                            className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Input
                            name="email"
                            type="email"
                            placeholder="Your Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                          />
                          <Input
                            name="subject"
                            type="text"
                            placeholder="Subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="bg-transparent border-border/50 rounded-xl h-12 px-4 placeholder:text-muted-foreground/60 focus:border-primary"
                          />
                        </div>

                        <Textarea
                          name="message"
                          placeholder="Your Message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="bg-transparent border-border/50 rounded-xl px-4 py-3 placeholder:text-muted-foreground/60 focus:border-primary resize-none"
                        />

                        <Button
                          type="submit"
                          disabled={isSubmitting || submitStatus === 'success'}
                          className={`w-full h-14 rounded-full bg-gradient-primary hover:opacity-90 text-primary-foreground font-semibold text-base group ${submitStatus === 'success' ? 'hidden' : ''}`}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin mr-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              Send Message
                              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                            </>
                          )}
                        </Button>

                        {/* Success Message - Beautiful Thank You */}
                        {submitStatus === 'success' && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                            className="text-center py-6"
                          >
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                              className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30"
                            >
                              <CheckCircle className="w-8 h-8 text-white" />
                            </motion.div>
                            <motion.h3
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 }}
                              className="text-xl sm:text-2xl font-bold text-foreground mb-2"
                            >
                              Thank You! 🎉
                            </motion.h3>
                            <motion.p
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 }}
                              className="text-muted-foreground mb-1"
                            >
                              Your message has been sent!
                            </motion.p>
                            <motion.p
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                              className="text-sm text-primary font-medium"
                            >
                              I'll get back to you soon.
                            </motion.p>
                          </motion.div>
                        )}

                        {submitStatus === 'error' && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-500 text-sm text-center"
                          >
                            Failed to send message. Please try again.
                          </motion.div>
                        )}
                      </form>
                    ) : (
                      /* Terms/Privacy Content */
                      <div className="space-y-6">
                        {(activeModal === 'terms' ? termsContent.sections : privacyContent.sections).map((section, index) => (
                          <div key={index} className="space-y-3">
                            <h3 className="text-lg font-semibold text-foreground">
                              {section.heading}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                              {section.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Modal Footer - only for Terms/Privacy */}
                  {activeModal !== 'contact' && (
                    <div className="p-6 border-t border-white/10 bg-white/5 modal-footer">
                      <div className="flex justify-end">
                        <Button
                          onClick={() => setActiveModal(null)}
                          className="px-6 bg-gradient-primary hover:opacity-90"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export { ContactSection };
